// createRoutine — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-routine' })` op (DSE2 §3.D7).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI.
//
// Per-engine surface (per DSE2 dialect.routine):
//   - mssql:  CREATE [OR ALTER] {PROCEDURE|FUNCTION} <name> [(params)]
//             [RETURNS <type>] [WITH …] AS <body>
//             — function params are IN-only.
//   - pgsql:  CREATE [OR REPLACE] {PROCEDURE|FUNCTION} <name>(params)
//             [RETURNS <type>] LANGUAGE <lang> [VOLATILE|STABLE|IMMUTABLE]
//             [STRICT] [SECURITY {DEFINER|INVOKER}] AS <delim>body<delim>
//   - mysql:  CREATE [DEFINER = …] {PROCEDURE|FUNCTION} <name>(params)
//             [RETURNS <type>] [DETERMINISTIC] [<sql-access>]
//             [SQL SECURITY {DEFINER|INVOKER}] <body>
//             — function params are IN-only; param defaults rejected.
//   - oracle: CREATE [OR REPLACE] [EDITIONABLE|NONEDITIONABLE]
//             {PROCEDURE|FUNCTION} <name>(params) [RETURN <type>]
//             [AUTHID {DEFINER|CURRENT_USER}] AS <body>
//   - sqlite: not supported — synthesizer throws.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, RoutineShellDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateRoutineArgs {
  readonly routine: RoutineShellDef;
}

export function buildCreateRoutineEdit(args: CreateRoutineArgs): DdlEdit {
  return { op: 'create-routine', routine: args.routine };
}

export async function commitCreateRoutine(
  ctx: VqeDdlEditContext,
  args: CreateRoutineArgs,
): Promise<void> {
  const edit = buildCreateRoutineEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const CREATE_ROUTINE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'mysql', 'oracle',
] as const);

/** Four engines support CREATE PROCEDURE / FUNCTION. sqlite has no stored
 *  routines — the synthesizer throws with a documented redirect. */
export function isCreateRoutineSupported(engine: SqlEngine): boolean {
  return CREATE_ROUTINE_SUPPORTED.has(engine);
}

export function getCreateRoutineUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return 'SQLite has no stored routines (no CREATE PROCEDURE / FUNCTION). '
      + 'Move logic to application code, or use a SQL view if read-only.';
  }
  return '';
}

/** Pre-flight validator mirroring DSE2's per-engine reject paths. Returns
 *  `null` when args are acceptable, else a user-facing message. */
export function validateCreateRoutineForEngine(
  engine: SqlEngine,
  args: CreateRoutineArgs,
): string | null {
  if (!isCreateRoutineSupported(engine)) {
    return getCreateRoutineUnsupportedTooltip(engine);
  }
  const r = args.routine;
  if (r.name.name.trim().length === 0) {
    return 'Routine name is required.';
  }
  if (r.bodyText.trim().length === 0) {
    return 'Routine body is required.';
  }
  if (r.kind === 'function' && (!r.returnsText || r.returnsText.trim().length === 0)) {
    return 'Functions require a RETURNS type.';
  }
  if (r.kind === 'procedure' && r.returnsText) {
    return 'Procedures must not declare a RETURNS type.';
  }
  if (r.kind === 'function') {
    for (const p of r.params) {
      if (engine === 'mssql' && (p.mode === 'out' || p.mode === 'inout')) {
        return 'mssql function params are IN-only — use kind:procedure for OUT / INOUT.';
      }
      if (engine === 'mysql' && (p.mode === 'out' || p.mode === 'inout')) {
        return 'mysql function params are IN-only — use kind:procedure for OUT / INOUT.';
      }
    }
  }
  if (engine === 'mysql') {
    for (const p of r.params) {
      if (p.defaultExpr) {
        return `mysql routine params do not accept defaults (param '${p.name}'). `
          + `Move the default into the body via 'IF param IS NULL THEN …'.`;
      }
    }
  }
  if (engine === 'postgres' && (!r.pgsqlLanguage || r.pgsqlLanguage.trim().length === 0)) {
    return `PostgreSQL requires a LANGUAGE clause (e.g. 'plpgsql', 'sql').`;
  }
  if (engine !== 'mssql' && r.mssqlOrAlter) {
    return `${engine} does not support OR ALTER (mssql-only).`;
  }
  if (engine === 'mssql' && r.orReplace) {
    return `mssql does not support OR REPLACE — use 'OR ALTER' instead.`;
  }
  if (engine === 'mysql' && r.orReplace) {
    return `mysql does not support OR REPLACE on routines — drop and recreate.`;
  }
  if (engine !== 'postgres' && r.pgsqlLanguage) {
    return `${engine} does not accept LANGUAGE (PostgreSQL-only).`;
  }
  if (engine !== 'postgres' && (r.pgsqlVolatility || r.pgsqlStrict)) {
    return `${engine} does not accept VOLATILITY / STRICT (PostgreSQL-only).`;
  }
  if (r.kind === 'procedure' && (r.pgsqlVolatility || r.pgsqlStrict)) {
    return `Volatility and STRICT only apply to PostgreSQL functions.`;
  }
  if (engine === 'mssql' && r.pgsqlSecurity) {
    return `mssql uses EXECUTE AS via WITH options, not SECURITY {DEFINER|INVOKER}.`;
  }
  if (engine !== 'mysql' && (r.mysqlDefiner || r.mysqlDeterministic
      || r.mysqlSqlAccess || r.mysqlSqlSecurity)) {
    return `${engine} does not accept mysql-specific routine options.`;
  }
  if (engine !== 'oracle' && (r.oracleEditionable || r.oracleNonEditionable
      || r.oracleAuthid)) {
    return `${engine} does not accept Oracle-specific routine options.`;
  }
  if (r.oracleEditionable && r.oracleNonEditionable) {
    return 'EDITIONABLE and NONEDITIONABLE are mutually exclusive.';
  }
  return null;
}
